// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/pcpService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import { paramsDaLista, type ListaFiltros, type PaginadoComResumo, type PcpListaResumo } from './listaFiltros';
import type {
  PcpOrdemResumo,
  PcpOrdemDetalhe,
  CriarPcpOrdemRequest,
  AtualizarPcpOrdemRequest,
  ConcluirPcpOrdemRequest,
  ConcluirPcpOrdemResponse,
  PaginadoResponse,
  PcpFiltros,
  PcpKpis,
} from '../types';

const BASE = '/api/pcp';

/** Filtros da listagem: os da paridade + os da v118 (período de emissão e ordenação). */
export type PcpFiltrosLista = PcpFiltros & Omit<ListaFiltros, 'busca' | 'status'> & { direcao?: 'asc' | 'desc' };

/** Remove filtros vazios para não mandar `?status=&busca=` à API. */
const limpar = (f: PcpFiltrosLista = {}) =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined && v !== '' && v !== null));

export const pcpService = {
  /**
   * Lista paginada + `resumo` (KPIs v118). Filtros da paridade (de/ate = previsão de entrega,
   * cliente, produto, etapa) e da v118 (dtInicio/dtFim = emissão, ordenarPor, decrescente/direcao).
   */
  async getAll(pagina = 1, tamanhoPagina = 20, filtros: PcpFiltrosLista = {}): Promise<PaginadoComResumo<PcpOrdemResumo, PcpListaResumo>> {
    const { data } = await apiClient.get<PaginadoComResumo<PcpOrdemResumo, PcpListaResumo>>(BASE, {
      params: { ...paramsDaLista(pagina, tamanhoPagina), ...limpar(filtros) },
    });
    return data;
  },

  async getKpis(filtros: Omit<PcpFiltros, 'status'> = {}): Promise<PcpKpis> {
    const { data } = await apiClient.get<PcpKpis>(`${BASE}/kpis`, { params: limpar(filtros) });
    return data;
  },

  /** Ordens por período (yyyy-MM-dd). `campo: 'entrega'` usa a previsão de entrega. */
  async getPorPeriodo(inicio: string, fim: string, campo: 'emissao' | 'entrega' = 'emissao'): Promise<PcpOrdemResumo[]> {
    const { data } = await apiClient.get<PcpOrdemResumo[]>(`${BASE}/periodo`, { params: { inicio, fim, campo } });
    return data;
  },

  async getAbertas(): Promise<PcpOrdemResumo[]> {
    const { data } = await apiClient.get<PcpOrdemResumo[]>(`${BASE}/abertas`);
    return data;
  },

  async getById(id: number): Promise<PcpOrdemDetalhe> {
    const { data } = await apiClient.get<PcpOrdemDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async create(request: CriarPcpOrdemRequest): Promise<PcpOrdemDetalhe> {
    const { data } = await apiClient.post<PcpOrdemDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarPcpOrdemRequest): Promise<PcpOrdemResumo> {
    const { data } = await apiClient.put<PcpOrdemResumo>(`${BASE}/${id}`, request);
    return data;
  },

  async concluir(id: number, request: ConcluirPcpOrdemRequest = {}): Promise<ConcluirPcpOrdemResponse> {
    const { data } = await apiClient.post<ConcluirPcpOrdemResponse>(`${BASE}/${id}/concluir`, request);
    return data;
  },

  /** Produção sob encomenda: gera a OP com os itens produzidos do pedido de venda. */
  async gerarDePedido(davId: number): Promise<PcpOrdemDetalhe> {
    const { data } = await apiClient.post<PcpOrdemDetalhe>(`${BASE}/de-pedido/${davId}`);
    return data;
  },

  /** Abre a Ordem de Produção em PDF (para o chão de fábrica) em uma nova aba. */
  async abrirPdf(id: number): Promise<void> {
    const { data } = await apiClient.get<Blob>(`${BASE}/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    // O navegador precisa da URL viva enquanto carrega o PDF na aba nova.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },
};
